/**
 * SmartErrorBoundary — capture les crashes React et adapte le rendu selon
 * l'audience :
 *
 *   - Utilisateurs INTERNES METERIS (`is_external === false` + rôle interne) :
 *     affichage debug complet avec message d'erreur, componentStack, et un
 *     bouton "Copier l'erreur" pour rapport de bug rapide.
 *
 *   - Utilisateurs externes (clients, partenaires) ou non-connectés :
 *     fallback sobre, sans détails techniques, juste un bouton "Recharger".
 *
 * Pattern recommandé pour wrapper toutes les pages détail/édition sensibles
 * (catalogue, CRM, ticketing, ...) afin que les bugs futurs soient signalés
 * aux techs sans paniquer les clients.
 *
 * Limitation React : un ErrorBoundary doit être une classe. On expose donc
 * une enveloppe fonctionnelle qui lit le user via useAuth() et le passe en
 * prop à la classe interne.
 */
import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useOrg } from './OrgSelector';
import { isUserInternalForOrg } from '../lib/roles';

class _SmartErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // Trace dans la console quoi qu'il arrive (utile pour les devtools).
    console.error('[SmartErrorBoundary]', error);
    console.error('[SmartErrorBoundary stack]', info?.componentStack);
    this.setState({ info });
  }

  handleCopy = async () => {
    const { error, info } = this.state;
    const payload = [
      `Erreur : ${error?.message || error}`,
      '',
      `URL : ${window.location.href}`,
      `Date : ${new Date().toISOString()}`,
      '',
      'Stack composant :',
      info?.componentStack || '(indisponible)',
      '',
      'Stack JS :',
      error?.stack || '(indisponible)',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(payload);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Fallback si clipboard API indisponible (navigateurs anciens, contexte
      // non sécurisé) : on sélectionne le texte du bloc <pre> pour Ctrl+C.
      const range = document.createRange();
      const sel = window.getSelection();
      const pre = document.getElementById('smart-eb-payload');
      if (pre && sel) {
        range.selectNodeContents(pre);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { user, label, orgCode } = this.props;
    const isInternal = isUserInternalForOrg(user, orgCode);

    // ── Fallback CLIENT (externe ou non connecté) ────────────────────────
    // Le but est qu'il ne voie pas un message technique qui fait peur. On
    // garde un visuel sobre, neutre, et un seul bouton "Recharger".
    if (!isInternal) {
      return (
        <div style={{ padding: '60px 24px', textAlign: 'center', fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div style={{ maxWidth: 420, margin: '0 auto', color: '#475569' }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: '0 0 8px', fontSize: 16, color: '#1e293b' }}>
              Cette page n'a pas pu s'afficher
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
              Une erreur temporaire est survenue. Tu peux essayer de recharger la page.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#1e3a8a', color: '#fff', border: 'none',
                borderRadius: 6, padding: '8px 18px', fontSize: 13,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Recharger
            </button>
          </div>
        </div>
      );
    }

    // ── DEBUG TECH (interne METERIS) ─────────────────────────────────────
    // Affichage complet avec copier-coller pour rapport de bug rapide.
    const errorMessage = String(this.state.error?.message || this.state.error);
    return (
      <div style={{ padding: 24, maxWidth: 960, margin: '0 auto', fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 20, color: '#7f1d1d' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ fontSize: 28 }}>🐛</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, color: '#dc2626', fontSize: 16 }}>
                Crash {label ? `— ${label}` : ''} <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(debug visible aux techs METERIS uniquement)</span>
              </h3>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {window.location.pathname} · {new Date().toLocaleTimeString('fr-FR')}
              </div>
            </div>
            <button
              onClick={this.handleCopy}
              style={{
                background: this.state.copied ? '#16a34a' : '#dc2626',
                color: '#fff', border: 'none', borderRadius: 6,
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
              title="Copier le rapport d'erreur complet pour déclarer un bug"
            >
              {this.state.copied ? '✓ Copié' : '📋 Copier l\'erreur'}
            </button>
          </div>

          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, whiteSpace: 'pre-wrap', background: 'white', padding: 12, borderRadius: 6, marginBottom: 10, color: '#7f1d1d', wordBreak: 'break-word' }}>
            {errorMessage}
          </div>

          {this.state.info?.componentStack && (
            <details style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Stack du composant
              </summary>
              <pre style={{ background: 'white', padding: 10, borderRadius: 4, overflowX: 'auto', marginTop: 6, fontSize: 11 }}>
                {this.state.info.componentStack}
              </pre>
            </details>
          )}

          {this.state.error?.stack && (
            <details style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Stack JavaScript
              </summary>
              <pre style={{ background: 'white', padding: 10, borderRadius: 4, overflowX: 'auto', marginTop: 6, fontSize: 11 }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}

          {/* Bloc invisible pour le fallback de selection si clipboard API échoue. */}
          <pre id="smart-eb-payload" style={{ position: 'absolute', left: -9999, top: -9999 }} aria-hidden>
            {`Erreur : ${errorMessage}\nURL : ${window.location.href}\nDate : ${new Date().toISOString()}`}
          </pre>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => window.location.reload()}
              style={{ background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Recharger
            </button>
            <button
              onClick={() => window.history.back()}
              style={{ background: '#fff', color: '#1e3a8a', border: '1px solid #1e3a8a', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default function SmartErrorBoundary({ children, label }) {
  const { user } = useAuth();
  // Piège 2 : sans le code de l'org active, un interne dont le rôle vient de
  // roles_by_org recevait le fallback « client » (sans stack ni bouton copier).
  const org = useOrg();
  const orgCode = org?.activeOrg?.code;
  return (
    <_SmartErrorBoundaryInner user={user} orgCode={orgCode} label={label}>
      {children}
    </_SmartErrorBoundaryInner>
  );
}
